import React from "react";

/**
 * Iter 19.68.7 — Filter-Chips für die Vordienst-Liste in Step 3.
 *
 * Single-Select-Chip-Leiste mit 4 Filtern:
 *   • Alle (default, kein Filter)
 *   • Anrechenbar (row.anrechenbar === true)
 *   • Nicht anrechenbar (row.anrechenbar === false)
 *   • Prüfen (Quality-Warning ungeprüft ODER Praktikum unentschieden
 *     ODER Gleichwertigkeit zu prüfen)
 *
 * Filter beeinflusst NUR die visuelle Liste — die Math-Engine berechnet
 * weiterhin alle Zeilen, der Step-4-Payload und der PDF-Export enthalten
 * den vollen Datensatz unabhängig vom aktiven Filter.
 *
 * Counts zeigen die Anzahl der Zeilen pro Kategorie. Klick auf einen
 * aktiven Chip dekativiert ihn nicht (= „Alle" muss explizit gewählt
 * werden) — vermeidet versehentliches Unfiltern.
 */
const CHIP_BASE = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11.5px] font-medium transition-crisp select-none cursor-pointer";
// Iter 20.03.P5-fix Runde 7 — Aktive-Chip-Farbe von Amber auf neutrales
// Ink-800 geändert. Amber wird jetzt konsequent nur für echte Warnungen
// verwendet (Gleichwertigkeit, Auslese-Unsicherheit, Praktikum-Warndots),
// damit der User „amber = etwas ist zu prüfen" konsistent lernt.
// Ein aktiver Filter ist keine Warnung, sondern ein ruhiger Zustand.
const CHIP_ACTIVE = "bg-ink-900 border-ink-900 text-white";
const CHIP_INACTIVE = "bg-white border-warm-300 text-warm-700 hover:bg-warm-50 hover:border-warm-400";
// Iter 20.36.f14.15 — Signal-Amber-Varianten für den „Prüfen"-Chip, wenn
// tatsächlich offene Prüfungen vorliegen (count > 0). Der neutrale
// Ink-Stil bleibt, wenn nichts zu prüfen ist (aktiver Filter im „alles
// geprüft"-Zustand) — konsistent mit dem UX-Grundsatz „amber = zu tun".
const CHIP_ATTENTION_ACTIVE = "bg-amber-500 border-amber-500 text-white";
const CHIP_ATTENTION_INACTIVE = "bg-amber-50 border-amber-400 text-amber-900 hover:bg-amber-100";
const COUNT_BASE = "inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[10px] font-semibold leading-none";
const COUNT_ACTIVE = "bg-white text-ink-900";
const COUNT_INACTIVE = "bg-warm-100 text-warm-600";
const COUNT_ATTENTION_ACTIVE = "bg-white text-amber-700";
const COUNT_ATTENTION_INACTIVE = "bg-amber-500 text-white";

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "anrechenbar", label: "Anrechenbar" },
  { key: "not_anrechenbar", label: "Nicht anrechenbar" },
  { key: "needs_attention", label: "Prüfen" },
];

export default function FilterChips({ active, counts, onChange }) {
  return (
    <div
      data-testid="step3-filter-chips"
      role="group"
      aria-label="Vordienst-Liste filtern"
      className="mb-2 flex flex-wrap items-center gap-1.5"
    >
      {FILTERS.map(({ key, label }) => {
        const isActive = active === key;
        const count = counts?.[key] ?? 0;
        // Iter 20.36.f14.15 — Der „Prüfen"-Chip signalisiert amber, wenn
        // count > 0 (offene Prüfungen). Andere Chips bleiben neutral.
        const attentionMode = key === "needs_attention" && count > 0;
        const chipCls = attentionMode
          ? (isActive ? CHIP_ATTENTION_ACTIVE : CHIP_ATTENTION_INACTIVE)
          : (isActive ? CHIP_ACTIVE : CHIP_INACTIVE);
        const countCls = attentionMode
          ? (isActive ? COUNT_ATTENTION_ACTIVE : COUNT_ATTENTION_INACTIVE)
          : (isActive ? COUNT_ACTIVE : COUNT_INACTIVE);
        return (
          <button
            key={key}
            type="button"
            data-testid={`step3-filter-chip-${key}`}
            data-active={isActive ? "true" : "false"}
            data-attention={attentionMode ? "true" : "false"}
            onClick={() => onChange(key)}
            aria-pressed={isActive}
            className={`${CHIP_BASE} ${chipCls}`}
          >
            <span>{label}</span>
            <span
              className={`${COUNT_BASE} ${countCls}`}
              data-testid={`step3-filter-chip-${key}-count`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
